import { Editor as CoreEditor, Editor } from "@tiptap/core";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import DragHandle from "@tiptap/extension-drag-handle-react";
import { EditorContent, EditorOptions, useEditor } from "@tiptap/react";
import { delay } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdOutlineDragIndicator } from "react-icons/md";
import { useLocation } from "react-router-dom";
import { ArtifactType } from "types/graphql-schema";

import { currentUserVar, editorVersionVar } from "@cache/cache";
import { useLink } from "@components/link/link-helpers";
import { classNames } from "@helpers/css";

import BubbleMenu from "./extensions/bubble-menu";
import CommentPopover from "./extensions/comment/comment-popover";
import FixedMenu from "./extensions/fixed-menu";
import { cursorColors, getExtensions, isEmptyValue } from "./helpers";
import { allSuggestion } from "./mentions/all-suggestions";
import WebsocketStatus from "./websocket-status";
import WYSIWYGOverlayDefaultNotes from "./wysiwyg-overlay-default-notes";

const ALLOWED_NODES_FOR_DRAG_HANDLE = [
  "action_item",
  "asana",
  "codeBlock",
  "decision",
  "document",
  "explorer",
  "feedback",
  "goal",
  "githubIssue",
  "iframe",
  "image",
  "kpi",
  "kpi-embed",
  "kpi-group-embed",
  "meeting-embed",
  "rating",
  "recognition",
  "timer",
  "url-preview",
  "button",
];

const WYSIWYG = ({
  value,
  overlayValue,
  editable,
  className,
  placeholder = "Type '/' to format the text...",
  emptyPlaceholder = null,
  showPlaceholderOnlyWhenEditable = true,
  enableComment = false,
  showPlusButton = false,
  showFixedMenu = false,
  isInSidebar = false,
  fixedMenuShowFullScreen = false,
  uploadVariable,
  webSocketDocumentId,
  mentionsConfig,
  extraContext,
  tiptapAiJwt,
  ydoc,
  providerWebsocket,
  disableSlashCommands = false,
  disableBubbleMenuArtifactActions = false,
  onUpdateContent,
  onFocus,
  onBlur,
  onModEnter,
  onEnter,
  hideStatusBanner = false,
  onWebsocketMessageChange,
}: {
  tiptapAiJwt?: string;
  ydoc?: any;
  providerWebsocket?: any;
  className?: string;
  enableComment: boolean;
  editable: boolean;
  showPlusButton?: boolean;
  showFixedMenu?: boolean;
  fixedMenuShowFullScreen?: boolean;
  isInSidebar?: boolean;
  placeholder?: string;
  disableSlashCommands?: boolean;
  disableBubbleMenuArtifactActions?: boolean;
  emptyPlaceholder?: string | null;
  showPlaceholderOnlyWhenEditable?: boolean;
  uploadVariable: any;
  value: any;
  overlayValue?: any;
  extraContext: {
    topicId?: number;
    relatedTopicId?: number;
    meetingId?: number;
    meetingDate?: string;
    meetingGroupId?: number;
    organizationId?: number;
    relatedArtifactId?: number;
    relatedArtifactType?: ArtifactType;
  };
  webSocketDocumentId?: string;
  mentionsConfig?: {
    meetingGroupId?: number;
    meetingId?: number;
    artifactId?: number;
  };
  onFocus?: EditorOptions["onFocus"];
  onBlur?: EditorOptions["onBlur"];
  onUpdateContent?: (evt: { editor: CoreEditor }) => void;
  onModEnter?: () => void;
  onEnter?: () => void;
  hideStatusBanner?: boolean;
  onWebsocketMessageChange?: (message: string | null) => void;
}) => {
  const link = useLink();
  const location = useLocation();
  const currentUser = currentUserVar();
  const editorVersion = editorVersionVar();
  const onModEnterRef = useRef(onModEnter);
  onModEnterRef.current = onModEnter;
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;
  const [showDefaultNotesOverlay, setShowDefaultNotesOverlay] = useState(
    !isEmptyValue(overlayValue) && isEmptyValue(value),
  );
  const [displayedCommentUuid, setDisplayedCommentUuid] = useState<
    string | null
  >(null);
  const { id, name, avatar } = currentUser;

  // Handling realtime
  const editorVersionsAreDifferent =
    // sometime value can be null while loading
    editorVersion.oldVersion !== null &&
    editorVersion.newVersion !== null &&
    String(editorVersion.oldVersion) !== String(editorVersion.newVersion);
  const isRealtimeEditorSchemaUpToDate = !editorVersionsAreDifferent;

  // need to refactor this at some point
  const atMentionSuggestions = useMemo(
    () => (mentionsConfig ? allSuggestion(mentionsConfig) : null),
    [mentionsConfig],
  );
  const commentAtMentionSuggestions = mentionsConfig
    ? allSuggestion({
        ...mentionsConfig,
        hideSearchModal: true,
        onlyUsers: true,
      })
    : null;

  const context = useMemo(
    () => ({
      ...extraContext,
      currentUser: { id, name, avatar },
    }),
    [extraContext, id, name, avatar],
  );

  // Name on this user's cursor for other editors, e.g. "Maxime P". Users
  // without a first/last name (e.g. not yet synced) fall back to `name`.
  const userName =
    [currentUser.firstName, currentUser.lastName?.[0]]
      .filter(Boolean)
      .join(" ") || currentUser.name;
  const color = cursorColors[currentUser.id % cursorColors.length];
  const extensions = useMemo(
    () =>
      getExtensions({
        tiptapAiJwt,
        context,
        atMentionSuggestions,
        paidFeatures: currentUser.paidFeatures,
        placeholder,
        showPlaceholderOnlyWhenEditable,
        emptyPlaceholder,
        history: false,
        disableSlashCommands,
        uploadVariable,
        onClickCommentUuid: setDisplayedCommentUuid,
        onModEnter: () => onModEnterRef.current?.(),
        onEnter: onEnter ? () => onEnterRef.current?.() : undefined,
      }),
    [
      context,
      disableSlashCommands,
      atMentionSuggestions,
      currentUser.paidFeatures,
      placeholder,
      showPlaceholderOnlyWhenEditable,
      emptyPlaceholder,
      uploadVariable,
      tiptapAiJwt,
    ],
  );
  const realtimeExtensions = useMemo(
    () =>
      extensions.concat([
        Collaboration.configure({
          document: ydoc,
          field: webSocketDocumentId,
        }),
        providerWebsocket &&
          isRealtimeEditorSchemaUpToDate &&
          CollaborationCaret.configure({
            provider: providerWebsocket,
            user: {
              name: userName,
              color: color,
            },
          }),
      ]),
    [
      color,
      extensions,
      isRealtimeEditorSchemaUpToDate,
      providerWebsocket,
      userName,
      webSocketDocumentId,
      ydoc,
    ],
  );

  const setValueIfEditorIsEmpty = useCallback(
    (editor: CoreEditor, value: any) => {
      // if editor is synced with websocket but has no content and
      // we have some data saved in topic.discussionNotes then set content
      delay(() => {
        // wait a little to give a chance to websocket to load data
        if (
          isRealtimeEditorSchemaUpToDate &&
          editor.isEmpty &&
          !isEmptyValue(value)
        ) {
          editor.chain().setContent(value, { emitUpdate: false }).run();
        }
      }, 1000);
    },
    [isRealtimeEditorSchemaUpToDate],
  );

  const handleTransaction = useCallback(
    ({ editor }: { editor: Editor }) => {
      if (showDefaultNotesOverlay && editor.getText()) {
        setShowDefaultNotesOverlay(false);
      }
    },
    [showDefaultNotesOverlay],
  );

  const handleSetDefaultValueOnCreation = useCallback(
    ({ editor }: { editor: Editor }) => {
      if (providerWebsocket) {
        // if the websocket is not yet synced, wait for that to happen and then
        // see if there is content in the database that should be seeded into
        // the editor
        if (!providerWebsocket.isSynced) {
          providerWebsocket?.on("synced", () => {
            setValueIfEditorIsEmpty(editor, value);
          });
        } else {
          // or, if the websocket is _already_ synced, for example if a new
          // topic was just added to a meeting that already has topics, can
          // just check immediately whether database content needs to be added.
          // this scenario won't fire the websocket `sync` event that is handled
          // above, because a single websocket can sync multiple editor instances
          // (i.e. multiple topics)
          setValueIfEditorIsEmpty(editor, value);
        }
      }
    },
    [providerWebsocket, setValueIfEditorIsEmpty, value],
  );

  const editorContent = useMemo(() => {
    return isRealtimeEditorSchemaUpToDate ? null : value;
  }, [isRealtimeEditorSchemaUpToDate, value]);

  const editor = useEditor(
    {
      injectNonce: window.TF?.cspNonce,
      // Tiptap 3 stops re-rendering on every transaction by default. Our
      // menus read editor state during render, so keep the Tiptap 2 behaviour.
      shouldRerenderOnTransaction: true,
      autofocus: false,
      editable: editable && isRealtimeEditorSchemaUpToDate,
      // when creating/updating extensions, never rename extension.name
      // otherwise previous discussion notes won't be able to
      // render the content of that extension.
      extensions: realtimeExtensions,
      editorProps: {
        attributes: {
          class: classNames(
            "prose max-w-full pl-7 focus:outline-hidden wrap-break-word relative js-topic-discussion-notes-input",
            className,
          ),
          "data-testid": "wysiwyg",
        },
        ...context,
      },
      // when WebRTC enable, it seems we should not use content
      // https://github.com/ueberdosis/tiptap/discussions/2193
      content: editorContent,
      onTransaction: handleTransaction,
      onCreate: handleSetDefaultValueOnCreation,

      // weirdly we need to do this otherwise it generates an error:
      // Cannot read properties of null (reading 'apply')
      // when onFocus or onBlur is undefined
      ...(onFocus ? { onFocus } : {}),
      ...(onBlur ? { onBlur } : {}),
    },
    // force reload of editor when it becomes out of date and need to be disabled
    [
      isRealtimeEditorSchemaUpToDate,
      editable,
      context,
      className,
      realtimeExtensions,
    ],
  );

  const handleSetDefaultNotes = () => {
    setShowDefaultNotesOverlay(false);
    if (editor)
      editor
        .chain()
        .setContent(overlayValue, { emitUpdate: false })
        .focus()
        .run();
  };

  useEffect(() => {
    if (onUpdateContent) {
      editor?.on("update", onUpdateContent);
    }
    return function cleanup() {
      if (onUpdateContent) {
        editor?.off("update", onUpdateContent);
      }
    };
  }, [onUpdateContent, editor]);

  // If there is a comment id in the url, we try to find it in editor
  // and display the popover and remove `comment` from the url query string
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const commentId = searchParams.get("inlineComment");
    if (isInSidebar && editor && commentId) {
      const selector = `[data-comment='${commentId}']`;
      delay(() => {
        const commentEl = editor?.view.dom.querySelector(selector);
        if (commentEl) {
          setDisplayedCommentUuid(commentId);
          commentEl.scrollIntoView();
        }
        const params = new URLSearchParams(location.search);
        params.delete("inlineComment");
        link.replace(`${location.pathname}?${params.toString()}`);
      }, 1000);
    }
  }, [editor, value]);

  const [hideHandle, setHideHandle] = useState(false);

  const handleChangeNode = useCallback(({ node }: { node: any }) => {
    const nodeTypeName = node?.type?.name;
    const shouldHide = nodeTypeName
      ? !ALLOWED_NODES_FOR_DRAG_HANDLE.includes(node.type.name)
      : false;

    setHideHandle((prev) => {
      if (prev === shouldHide) return prev; // no update, no re-render
      return shouldHide;
    });
  }, []);

  return (
    <>
      {/* DON'T REMOVE EXTRA DIV HERE, IT'S IMPORTANT FOR THE DRAG HANDLE TO WORK */}
      {showPlusButton && editable && editor && editor.isEditable && (
        <div>
          <DragHandle
            editor={editor}
            className="z-0"
            onNodeChange={handleChangeNode}
            // The default placement (`left-start`) anchors the handle to the
            // top of the block so it lines up with the artifact's first-row
            // controls, rather than the vertical center of a tall expanded
            // card. The handle stays visible while the cursor moves from
            // the block onto the handle, because the plugin only hides it
            // when the cursor leaves both the editor and the handle.
            // Do not pass an inline `computePositionConfig`: a new object on
            // each render makes the plugin re-register in a loop.
          >
            <span
              // Match the topic drag handle: no default background, light
              // gray hover, 2px padding (`p-0.5`) and a 16px icon.
              // `inline-flex` sizes the span to its content so the hover
              // background hugs the icon rather than stretching the line
              // box.
              className="inline-flex items-center justify-center text-gray-400 hover:bg-gray-100 rounded-sm p-0.5 cursor-grab"
              style={{
                opacity: hideHandle ? 0 : 1,
                pointerEvents: hideHandle ? "none" : "auto",
              }}
            >
              <MdOutlineDragIndicator className="w-4 h-4" />
            </span>
          </DragHandle>
        </div>
      )}
      {editor && !editor.isDestroyed && (
        <BubbleMenu
          editor={editor}
          meetingId={context.meetingId}
          topicId={extraContext.relatedTopicId}
          organizationId={context.organizationId}
          enableComment={enableComment}
          onCreateCommentUuid={setDisplayedCommentUuid}
          disableBubbleMenuArtifactActions={disableBubbleMenuArtifactActions}
        />
      )}
      {showFixedMenu && editor && !editor.isDestroyed && (
        <FixedMenu
          editor={editor}
          fixedMenuShowFullScreen={fixedMenuShowFullScreen}
          fullScreenArtifactId={context.relatedArtifactId}
          fullScreenArtifactType={context.relatedArtifactType}
        />
      )}
      {displayedCommentUuid && editor && (
        <CommentPopover
          editor={editor}
          uuid={displayedCommentUuid}
          topicId={context.topicId}
          artifactId={context.relatedArtifactId}
          atMentionSuggestions={commentAtMentionSuggestions}
          uploadVariable={uploadVariable}
          onClearUuid={() => setDisplayedCommentUuid(null)}
        />
      )}

      {showDefaultNotesOverlay && (
        <WYSIWYGOverlayDefaultNotes
          disabled={!editable}
          onSetDefaultNotes={handleSetDefaultNotes}
          overlayValue={overlayValue}
          extensions={extensions}
          extraContext={extraContext}
          className={className}
        />
      )}
      <div
        className={classNames(
          showDefaultNotesOverlay ? "hidden" : "block",
          "relative",
        )}
      >
        <EditorContent editor={editor} />
        {editor && !editor.isDestroyed && (
          <WebsocketStatus
            isRealtimeEditorSchemaUpToDate={isRealtimeEditorSchemaUpToDate}
            isFocused={editor.isFocused}
            // Hocuspocus 3 keeps the connection status on the shared
            // websocket, not on the document provider.
            status={providerWebsocket?.configuration.websocketProvider.status}
            hideBanner={hideStatusBanner}
            onMessageChange={onWebsocketMessageChange}
          />
        )}
      </div>
    </>
  );
};

export default WYSIWYG;
